import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { useAccounts } from '@/features/accounts/api/accounts-queries';
import { needsReconnect } from '@/shared/utils/account-health';
import styles from './CredentialAlert.module.scss';

export interface CredentialAlertProps {
  /** Ad account the campaign will launch through, when one is chosen yet. */
  adAccountId: string | undefined;
}

/**
 * Warns inside the wizard when the campaign's ad account can no longer reach
 * the platform.
 *
 * Without this the credential only fails at the point of use — a search that
 * returns nothing, or a launch that errors after the user has filled in every
 * step — with nothing connecting the failure to the account that caused it.
 * Managed accounts are skipped by `needsReconnect`: the organizer cannot fix
 * those and telling them to would be a dead end.
 */
export function CredentialAlert({ adAccountId }: CredentialAlertProps) {
  const { data: accounts } = useAccounts();

  // Before an account is picked there is nothing to warn about; the step's own
  // validation covers the "none selected" case.
  if (!adAccountId) return null;

  const account = accounts?.find((candidate) => candidate.id === adAccountId);
  if (!account || !needsReconnect(account)) return null;

  const expired = account.tokenStatus === 'expired';

  return (
    <div
      className={clsx(styles.alert, expired ? styles.alertExpired : styles.alertExpiring)}
      role="alert"
    >
      <AlertTriangle size={16} className={styles.icon} />
      <span className={styles.text}>
        {expired
          ? `${account.accountName} is disconnected, so this campaign cannot launch or load targeting data.`
          : `${account.accountName} has a connection expiring soon; reconnect it before launching.`}
      </span>
      <Link to="/promote/accounts" className={styles.link}>
        Fix in Accounts
      </Link>
    </div>
  );
}
